import { useEffect, useState } from "react";
import { Footer } from "@/components/landing/Footer";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Cookie, Lock } from "lucide-react";
import { useSeo } from "@/lib/seo";

const UPDATED = "AUG 2026";

const SECTIONS = [
  {
    id: "intro",
    num: "00",
    title: "Overview",
    body: [
      "This Privacy Policy explains how TradrQuant (\"TradrQuant,\" \"we,\" \"us\") collects, uses, and protects information when you use our website, Telegram channels, alerts, and related services (the \"Service\"). By using the Service you agree to this Policy. It should be read together with our Risk Notice & Terms of Service.",
      "We are a small, independent team. We collect as little as we can, we do not sell your personal data, and we tell you plainly what we do with what we hold.",
    ],
  },
  {
    id: "collect",
    num: "01",
    title: "Information We Collect",
    body: [
      "Information you give us: your email address when you join a waitlist or subscribe; any messages you send us; and payment details you provide to our payment processor (we never store full card numbers).",
      "Information collected automatically: basic device and usage data such as IP address, browser type, pages viewed, referring URLs, and timestamps, gathered via cookies and similar technologies (see Cookies below).",
      "Telegram data: if you join a TradrQuant Telegram channel, Telegram processes your account per its own privacy policy. We only see what Telegram exposes to channel operators (e.g., public username). We do not receive your phone number or private messages.",
    ],
  },
  {
    id: "use",
    num: "02",
    title: "How We Use Information",
    body: [
      "To operate, maintain, and improve the Service; to send you the emails you asked for (e.g., waitlist and early-access notices); to process payments; to analyze usage and performance; to prevent fraud and abuse; and to comply with legal obligations.",
      "We rely on legitimate interests, your consent (which you can withdraw), performance of a contract, and legal compliance as our lawful bases for processing where applicable.",
    ],
  },
  {
    id: "cookies",
    num: "03",
    title: "Cookies & Tracking",
    body: [
      "We use cookies and similar technologies. Essential cookies are required for the site to function and remember your choices (including your cookie and risk-acknowledgement preferences, which are stored locally on your device). Analytics cookies help us understand how the Service is used so we can improve it.",
      "You can accept or limit non-essential cookies via the cookie banner, and you can clear or block cookies in your browser settings at any time. Blocking essential cookies may break parts of the Service.",
    ],
  },
  {
    id: "sharing",
    num: "04",
    title: "How We Share Information",
    body: [
      "We do not sell your personal data. We share information only with: service providers who help us run the Service (e.g., hosting, email, analytics, and payment processors) under confidentiality obligations; authorities when required by law; and, in the event of a business transfer, a successor entity. Any third-party links you follow are governed by their own privacy policies.",
    ],
  },
  {
    id: "retention",
    num: "05",
    title: "Data Retention & Security",
    body: [
      "We keep personal data only as long as necessary for the purposes described here or as required by law, then delete or anonymize it. We use reasonable technical and organizational measures to protect your data, but no method of transmission or storage is 100% secure and we cannot guarantee absolute security.",
    ],
  },
  {
    id: "rights",
    num: "06",
    title: "Your Rights",
    body: [
      "Depending on where you live, you may have rights to access, correct, delete, or port your personal data, to object to or restrict certain processing, and to withdraw consent. You can unsubscribe from marketing emails at any time via the link in each email. To exercise any right, contact us through our official channels; we will respond as required by applicable law.",
      "We do not knowingly collect data from anyone under 18. If you believe a minor has provided us data, contact us and we will delete it.",
    ],
  },
  {
    id: "changes",
    num: "07",
    title: "Changes & Contact",
    body: [
      "We may update this Policy from time to time; the effective date above will reflect the latest version, and continued use of the Service means you accept the changes. Questions about this Policy can be directed to the TradrQuant team via our official Telegram channels. TradrQuant · @Tradernaut.",
    ],
  },
];

export default function Privacy() {
  const [cursor, setCursor] = useState(true);
  useSeo({
    title: "Privacy Policy — TradrQuant",
    description: "How TradrQuant collects, uses and protects your data. We collect as little as possible and never sell your personal data.",
    path: "/privacy",
  });
  useEffect(() => {
    const iv = setInterval(() => setCursor((c) => !c), 530);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "TradrQuant · Privacy Policy";
  }, []);

  return (
    <div data-testid="privacy-page" className="grain relative min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" data-testid="privacy-home-link" className="flex items-center gap-2.5 group">
            <img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto group-hover:scale-105 transition-transform duration-300" />
            <span data-testid="wordmark" className="tq-wordmark text-[#ECEBE4] text-[19px] sm:text-[21px]">Tradr<span className="tq-q">Q</span>uant</span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-btn"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 border border-white/15 px-3 py-2 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors duration-300"
          >
            <ChevronLeft size={14} /> cd ~/terminal
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1000px] px-5 md:px-10 pt-10 md:pt-16">
        <div className="relative border border-white/12 bg-[#0d0d0d]">
          <div className="flex items-center gap-2 border-b border-white/10 bg-[#111111] px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#BACD1C]/60" />
            <span className="ml-3 font-mono text-xs text-white/45 tracking-[0.15em] lowercase">legal/privacy_policy.md</span>
            <span className="ml-auto flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7D8390]">
              <Lock size={13} /> your data
            </span>
          </div>
          <div className="p-6 md:p-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">// how we handle your data</p>
            <h1 className="mt-4 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Privacy <span className="text-[#7D8390]">Policy</span>
              <span className="text-[#7D8390]">{cursor ? "_" : "\u00A0"}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-sm text-white/50 leading-relaxed">
              We collect as little as possible, we never sell your data, and we tell you exactly
              what we store — including cookies and how Telegram fits in.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/35">
              <span>&gt; last_updated: <span className="text-[#BACD1C]/80">{UPDATED}</span></span>
              <span className="flex items-center gap-1.5"><Cookie size={12} className="text-[#BACD1C]/70" /> sells_data: <span className="text-[#BACD1C]/80">false</span></span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1000px] px-5 md:px-10 py-12 md:py-16 space-y-14">
        {SECTIONS.map((s) => (
          <motion.section
            key={s.id}
            id={s.id}
            data-testid={`privacy-section-${s.id}`}
            className="scroll-mt-24"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">
              [{s.num}] // section.{s.id.replace(/-/g, "_")}()
            </p>
            <h2 className="mt-3 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl leading-tight">
              {s.title}
            </h2>
            <div className="mt-5 space-y-4 border-l border-white/10 pl-5">
              {s.body.map((p, i) => (
                <p key={i} className="text-[13px] sm:text-sm text-white/55 leading-relaxed">{p}</p>
              ))}
            </div>
          </motion.section>
        ))}

        <div className="flex flex-wrap gap-4 font-mono text-xs">
          <Link to="/terms" className="text-[#BACD1C]/70 hover:text-[#BACD1C] underline underline-offset-2">Risk Notice &amp; Terms →</Link>
          <Link to="/" className="text-white/40 hover:text-white underline underline-offset-2">Back to terminal →</Link>
        </div>
        <p className="font-mono text-xs text-white/25 tracking-[0.15em]">
          © {new Date().getFullYear()} TradrQuant · We never sell your data
        </p>
      </div>
      <Footer />
    </div>
  );
}
